import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FiFeather, FiTag, FiPackage, FiCheckCircle } from "react-icons/fi";
import { fadeUp, useMotion } from "../../ui/motion";
import * as S from "./FarmerJoinBand.styled";

/**
 * Recruitment band for farmers, sitting among buyer-facing content on the
 * homepage. Before this, the site had no seller entry point at all: /signup
 * defaults to a Client account and nothing anywhere said farmers could join.
 *
 * The primary action deep-links the role so the farmer lands on the right form
 * already selected — SignupPage reads ?role=villager.
 *
 * Copy says "apply", not "start selling today", because registration is followed
 * by an admin review step (RequireApprovedVillager). Promising instant selling
 * would be a promise the backend does not keep.
 */
const FarmerJoinBand = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reduce } = useMotion();

  const points = [
    {
      key: "prices",
      icon: <FiTag aria-hidden="true" />,
      label: t("villager.join.point_prices", { defaultValue: "You set your own prices" }),
    },
    {
      key: "orders",
      icon: <FiPackage aria-hidden="true" />,
      label: t("villager.join.point_orders", { defaultValue: "Manage stock and orders" }),
    },
    {
      key: "free",
      icon: <FiCheckCircle aria-hidden="true" />,
      label: t("villager.join.point_free", { defaultValue: "Free to apply" }),
    },
  ];

  return (
    <S.Band
      data-testid="farmer-join-band"
      aria-labelledby="farmer-join-title"
      variants={fadeUp(reduce)}
      initial="hidden"
      /* Animate on mount, not whileInView: this sits above the fold, and
         IntersectionObserver never fires in jsdom. */
      animate="show"
    >
      <S.Inner>
        <S.IconWrap aria-hidden="true">
          <FiFeather />
        </S.IconWrap>

        <S.Text>
          <S.Title id="farmer-join-title">
            {t("villager.join.title", { defaultValue: "Are you a farmer?" })}
          </S.Title>
          <S.Sub>
            {t("villager.join.subtitle", {
              defaultValue:
                "Sell your produce directly to families. Apply in a few minutes and set up your own farm page.",
            })}
          </S.Sub>
          <S.Points>
            {points.map((point) => (
              <S.Point key={point.key}>
                {point.icon}
                {point.label}
              </S.Point>
            ))}
          </S.Points>
        </S.Text>

        <S.Actions>
          <S.ApplyButton
            type="button"
            onClick={() => navigate("/signup?role=villager")}
            aria-label={t("villager.join.cta_aria", {
              defaultValue: "Apply to sell your produce on Dora e Fshatit",
            })}
          >
            {t("villager.join.cta", { defaultValue: "Apply to sell" })}
          </S.ApplyButton>
          <S.SignInLink type="button" onClick={() => navigate("/login")}>
            {t("villager.join.already", { defaultValue: "Already selling? Sign in" })}
          </S.SignInLink>
        </S.Actions>
      </S.Inner>
    </S.Band>
  );
};

export default FarmerJoinBand;
